// CardHeader — title row that sits at the top of a `Card`. Mirrors the
// `.card-header` / `.card-title` / `.card-link` rules at lines 305–321 of
// /Users/harry/Downloads/employee-portal/project/styles.css and the
// `CardHeader` function around line 169 of project/components.jsx.
//
// Layout: [title] [count?] [flex spacer] [action?]
//   - title row: flex, alignItems center, gap sm (8px), mb lg (16px)
//   - title:    fontFamily UI/body, fontSize md, fontWeight 600, neutral.950,
//               lineHeight 1, padding-left 1px (per components.jsx inline)
//   - count:    rendered via the shared `CountTag` ds primitive
//   - action:   inline link styled per `.card-link` — fontSize smd, weight 500,
//               p.600, gap 4px, p.700 on hover, no underline
//
// subtitle: an optional second line below the title row. Not in the original
// design's CardHeader signature but kept in the props per the spec — used as a
// place to put a small descriptor when the title alone is ambiguous.

import { Box } from "@chakra-ui/react";
import { ReactNode } from "react";
import { TbArrowRight } from "react-icons/tb";

import CountTag from "./CountTag";

export interface CardHeaderProps {
  title: ReactNode;
  /** Shown as a CountTag immediately after the title. */
  count?: number;
  /** Optional right-side action link (e.g. "View all →"). */
  action?: { label: string; onClick: () => void };
  /** Optional second line under the title row. */
  subtitle?: ReactNode;
}

const CardHeader = ({ title, count, action, subtitle }: CardHeaderProps) => {
  return (
    <Box>
      {/* design ref: styles.css:305–308 — `.card-header` */}
      <Box
        display="flex"
        alignItems="center"
        gap="sm" // 8px
        mb={subtitle ? "2xs" : "lg"} // 4px when subtitle present (compact stack), else 16px
      >
        {/* design ref: styles.css:309–314 — `.card-title` + components.jsx:172 inline */}
        <Box
          as="span"
          fontFamily="body"
          fontSize="md"
          fontWeight={600}
          color="neutral.950"
          lineHeight="1"
          pl="1px"
        >
          {title}
        </Box>

        {count !== undefined && <CountTag count={count} />}

        <Box flex="1" />

        {action && (
          // design ref: styles.css:315–321 — `.card-link`
          <Box
            as="button"
            type="button"
            onClick={action.onClick}
            display="inline-flex"
            alignItems="center"
            gap="2xs" // 4px
            fontSize="smd" // 13px
            fontWeight={500}
            color="p.600"
            bg="transparent"
            border="none"
            p={0}
            cursor="pointer"
            lineHeight="1"
            transition="color 120ms"
            _hover={{ color: "p.700", textDecoration: "none" }}
          >
            {action.label}
            <Box as={TbArrowRight} aria-hidden boxSize="14px" />
          </Box>
        )}
      </Box>

      {subtitle && (
        <Box
          fontSize="smd"
          color="neutral.700"
          lineHeight="1.4"
          mb="lg" // 16px — keeps the overall header-to-body rhythm
        >
          {subtitle}
        </Box>
      )}
    </Box>
  );
};

export default CardHeader;
